import { useChatStreaming } from "@/shared/hooks/useChatStreaming";
import { useEffect, useState, useCallback, useRef } from "react";
import { useChatScroll } from "@/shared/hooks/useChatScroll";

export const useChatController = ({
  userId,
  threadId = "",
  companyName = "",
  interactions = [],
  fetchChatHook,
  sendChatHook,
  onComplete,
  onProgress,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [pendingDisplay, setPendingDisplay] = useState(null);
  const [loading, setLoading] = useState(false);
  const pendingHistoryScrollRef = useRef(false);

  // Scroll logic
  const { conversationRef, scrollToBottom, isNearBottom } =
    useChatScroll();

  // Streaming logic
  const {
    handleStreamChunk,
    resetStreaming,
    streamingMessageIdRef,
  } = useChatStreaming(setMessages, scrollToBottom, isNearBottom);

  // Fetch history
  const { data: chatHistory } = fetchChatHook({
    userId,
    threadId,
    enabled: !!userId && !!threadId,
  });

  // Send message hook
  const {
    data: chatAnswer,
    isLoading: isAnswerLoading,
    stop: stopChat,
  } =
    sendChatHook({
      user_id: userId,
      thread_id: threadId,
      company_name: companyName,
      interactions_context: interactions,
      prompt: pendingPrompt,
      display: pendingDisplay,
      enabled: !!pendingPrompt && !!threadId,
      onStreamChunk: handleStreamChunk,
      onProgress,
    });

  // Load history — initial mount only. Once the conversation is live, local
  // state is the source of truth; a background refetch must not clobber
  // optimistic user messages or an in-flight stream.
  useEffect(() => {
    setMessages([]);
    setPendingPrompt(null);
    setPendingDisplay(null);
    setLoading(false);
    resetStreaming();
  }, [threadId, resetStreaming]);

  useEffect(() => {
    if (!chatHistory?.messages) return;
    setMessages((prev) => {
      // A freshly created thread has no history yet, but its first message is
      // already on screen. Never let that empty response wipe it.
      if (!chatHistory.messages.length && prev.length) return prev;
      pendingHistoryScrollRef.current = true;
      return chatHistory.messages;
    });
  }, [chatHistory, threadId]);

  // Handle stream completion
  useEffect(() => {
    if (chatAnswer && !isAnswerLoading) {
      setLoading(false);
      setPendingPrompt(null);
      setPendingDisplay(null);
      resetStreaming();
      onComplete?.();
    }
  }, [chatAnswer, isAnswerLoading, onComplete, resetStreaming]);

  // Auto scroll - when user sends, near bottom (streaming), or after history loads on mount
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessageFromUser = messages[messages.length - 1]?.role === "user";
    const shouldScroll =
      lastMessageFromUser ||
      isNearBottom() ||
      pendingHistoryScrollRef.current;

    if (!shouldScroll) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom();
        pendingHistoryScrollRef.current = false;
      });
    });
  }, [messages, isNearBottom, scrollToBottom]);

  const handleSend = useCallback(
    // displayText: what the user bubble shows (e.g. "Q: ...\nA: ..." from a
    // follow-up option); the backend still receives `content` for its logic.
    (promptText = null, displayText = null) => {
      const content = promptText || input.trim();
      if (!content || loading || !threadId) return;

      const userMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: displayText || content,
      };

      resetStreaming();

      setMessages((prev) => [
        ...prev.filter((m) => m !== ""),
        userMessage,
      ]);

      setPendingPrompt(content);
      setPendingDisplay(displayText);
      setInput("");
      setLoading(true);

      scrollToBottom();
    },
    [input, loading, resetStreaming, scrollToBottom, threadId]
  );

  const stop = useCallback(() => {
    stopChat?.();
    setLoading(false);
    setPendingPrompt(null);
    setPendingDisplay(null);
    resetStreaming();
  }, [resetStreaming, stopChat]);

  return {
    messages,
    input,
    setInput,
    handleSend,
    stop,
    loading: loading || isAnswerLoading,
    conversationRef,
    streamingMessageIdRef,
  };
};
